import React, { useEffect } from "react";

import { setGameData } from "../../../contexts/game/actions/set-game-data";

import { useLocalizedNamespace } from "../../../hooks/use-localized-namespace";

import { loadUntilItSucceeds } from "../../../utils/load-until-it-succeeds";

type WithLoadedDataProps = {
    context: {
        state: { settings: { game: Record<string, unknown>; search: string } };
        dispatch: (action: unknown) => void;
    };
    namespace: string;
    locale: { lang: string };
};

export const withLoadedData =
    <P extends WithLoadedDataProps>(
        Component: React.ComponentType<P & { data: unknown; error?: Error }>,
    ) =>
    (props: P) => {
        const { context, namespace, locale } = props;

        const { lang } = locale;
        const { state, dispatch } = context;
        const { settings } = state;
        const { game, search } = settings;

        const newSearch = new URLSearchParams(search);
        if (!newSearch.has("cachebust")) {
            newSearch.set("cachebust", Date.now().toString());
        }

        const url = `./data/${lang}/${namespace}/content.json?${newSearch}`;
        const localizedNamespace = `${lang}-${namespace}`;
        const data = game[localizedNamespace];

        const [error, setError] = React.useState<Error | undefined>();

        const [, setLocalizedNamespace] = useLocalizedNamespace();

        const onError = setError;

        const onLoad = (raw: Response) =>
            raw
                .json()
                .then((json: unknown) => {
                    dispatch(setGameData(localizedNamespace, json));
                })
                .catch(onError);

        useEffect(() => {
            setLocalizedNamespace(localizedNamespace);
        }, [localizedNamespace]);

        useEffect(() => {
            setError(undefined);

            if (!data) return loadUntilItSucceeds(url, onLoad, onError);
        }, [lang]);

        return <Component {...props} data={data} error={error} />;
    };
